import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RotateCw } from "lucide-react";
import { fetchNetworkStats } from "../services/networks";
import { fetchNews } from "../services/news";
import { fetchAdminPolls } from "../services/polls";
import { fetchFeedbacks, fetchUnreadAdminFeedbacks } from "../services/feedbacks";

const WidgetCard = ({ title, value, hint, onClick, className = "", hasBadge = false, badgeValue = null }) => {
    const clickable = typeof onClick === "function";
    const numericBadge = Number(badgeValue);
    const hasNumericBadge = Number.isFinite(numericBadge) && numericBadge > 0;
    const showBadge = Boolean(hasBadge || hasNumericBadge);
    const badgeLabel = hasNumericBadge ? numericBadge : 1;

    return (
        <button
            type="button"
            className={`widget-card ${clickable ? "widget-card--click" : ""} ${className}`}
            onClick={onClick}
            disabled={!clickable}
        >
            {showBadge ? (
                <span className="widget-card__badge widget-card__badge--count" aria-hidden="true">
                    {badgeLabel}
                </span>
            ) : null}
            <div className="widget-card__title">{title}</div>
            <div className="widget-card__value">{value}</div>
            {hint ? <div className="widget-card__hint">{hint}</div> : null}
        </button>
    );
};

const formatDate = (value) => {
    if (!value) return "";
    try {
        return new Date(value).toLocaleDateString("uk-UA", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    } catch (_) {
        return "";
    }
};

const previewText = (value, max = 90) => {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (!text) return "Без тексту";
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

export default function AdminWidgets({
    onNavigate,
    hasNews = false,
    hasPolls = false,
    hasUnreadFeedbacks = false,
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [stats, setStats] = useState({
        networks_total: 0,
        networks_active: 0,
        clients_total: 0,
        operators_total: 0,
        networks: [],
    });
    const [newsCount, setNewsCount] = useState(0);
    const [activePollsCount, setActivePollsCount] = useState(0);
    const [newFeedbacksCount, setNewFeedbacksCount] = useState(0);
    const [recentNews, setRecentNews] = useState([]);
    const [recentPolls, setRecentPolls] = useState([]);
    const [recentFeedbacks, setRecentFeedbacks] = useState([]);

    const load = useCallback(async (aliveRef) => {
        setLoading(true);
        setError("");
        try {
            const [networkStats, news, polls, feedbacks, unreadFeedbacks] = await Promise.all([
                fetchNetworkStats().catch(() => null),
                fetchNews().catch(() => []),
                fetchAdminPolls().catch(() => []),
                fetchFeedbacks().catch(() => []),
                fetchUnreadAdminFeedbacks().catch(() => ({ count: 0 })),
            ]);

            if (aliveRef && !aliveRef.current) return;

            const nextStats = networkStats && typeof networkStats === "object"
                ? {
                    networks_total: Number(networkStats.networks_total) || 0,
                    networks_active: Number(networkStats.networks_active) || 0,
                    networks_self_hosted: Number(networkStats.networks_self_hosted) || 0,
                    clients_total: Number(networkStats.clients_total) || 0,
                    operators_total: Number(networkStats.operators_total) || 0,
                    networks: Array.isArray(networkStats.networks) ? networkStats.networks : [],
                }
                : {
                    networks_total: 0,
                    networks_active: 0,
                    networks_self_hosted: 0,
                    clients_total: 0,
                    operators_total: 0,
                    networks: [],
                };

            const newsList = Array.isArray(news) ? news : [];
            const pollsList = Array.isArray(polls) ? polls : [];
            const feedbacksList = Array.isArray(feedbacks) ? feedbacks : [];
            const now = Date.now();
            const activePolls = pollsList.filter(
                (poll) => poll && poll.status !== false && poll.expires_at && new Date(poll.expires_at).getTime() > now
            );
            const unreadCount = Number(unreadFeedbacks?.count);
            const newFeedbacks = Number.isFinite(unreadCount)
                ? unreadCount
                : feedbacksList.filter((item) => String(item?.status || "").toLowerCase() === "new").length;

            setStats(nextStats);
            setNewsCount(newsList.filter((item) => item && item.status === true).length);
            setActivePollsCount(activePolls.length);
            setNewFeedbacksCount(newFeedbacks);
            setRecentNews(newsList.slice(0, 3));
            setRecentPolls(pollsList.slice(0, 3));
            setRecentFeedbacks(feedbacksList.slice(0, 3));
        } catch (e) {
            if (aliveRef && !aliveRef.current) return;
            setError(e?.message || "Не вдалося завантажити головну");
        } finally {
            if (!aliveRef || aliveRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        const aliveRef = { current: true };
        load(aliveRef);
        return () => {
            aliveRef.current = false;
        };
    }, [load]);

    const summaryCards = useMemo(() => ([
        {
            title: "Мережі",
            value: stats.networks_total,
            hint: `Активних: ${stats.networks_active}${stats.networks_self_hosted ? ` · self-hosted: ${stats.networks_self_hosted}` : ""}`,
            onClick: () => onNavigate?.("networks"),
        },
        {
            title: "Клієнти",
            value: stats.clients_total,
            hint: "Активні клієнти у всіх мережах",
            onClick: () => onNavigate?.("networks"),
        },
        {
            title: "Оператори",
            value: stats.operators_total,
            hint: "Активні оператори",
            onClick: () => onNavigate?.("operators"),
        },
        {
            title: "Новини",
            value: newsCount,
            hint: "Активні новини",
            onClick: () => onNavigate?.("news"),
            hasBadge: hasNews,
        },
        {
            title: "Опитування",
            value: activePollsCount,
            hint: "Активні опитування",
            onClick: () => onNavigate?.("polls"),
            hasBadge: hasPolls,
        },
        {
            title: "Відгуки",
            value: newFeedbacksCount,
            hint: newFeedbacksCount > 0 ? "Нові відгуки без відповіді" : "Нових відгуків немає",
            onClick: () => onNavigate?.("feedbacks"),
            className: newFeedbacksCount > 0 || hasUnreadFeedbacks ? "widget-card--attention" : "",
            badgeValue: newFeedbacksCount,
            hasBadge: hasUnreadFeedbacks,
        },
    ]), [
        stats.networks_total,
        stats.networks_active,
        stats.networks_self_hosted,
        stats.clients_total,
        stats.operators_total,
        newsCount,
        activePollsCount,
        newFeedbacksCount,
        onNavigate,
        hasNews,
        hasPolls,
        hasUnreadFeedbacks,
    ]);

    const networkRows = useMemo(() => (
        Array.isArray(stats.networks)
            ? [...stats.networks].sort((a, b) => (Number(b?.clients_count) || 0) - (Number(a?.clients_count) || 0))
            : []
    ), [stats.networks]);

    return (
        <div className="widgets-page">
            <div className="widgets-page__header config-header">
                <div className="header-buttons">
                    <button
                        type="button"
                        className="add-btn refresh toolbar-icon-btn"
                        onClick={() => load({ current: true })}
                        title="Оновити"
                        aria-label="Оновити"
                        disabled={loading}
                    >
                        <RotateCw size={16} />
                    </button>
                </div>
            </div>

            {error ? <div className="message-banner" style={{ marginBottom: 12 }}>{error}</div> : null}
            {loading ? <p>⏳ Завантаження...</p> : null}

            <div className="widgets-grid">
                {summaryCards.map((card) => (
                    <WidgetCard key={card.title} {...card} />
                ))}
            </div>

            <section className="admin-widgets-section">
                <div className="admin-widgets-section__head">
                    <h3>Статистика по мережах</h3>
                    <p>Скільки мереж у системі та кількість активних клієнтів у кожній.</p>
                </div>

                {networkRows.length === 0 ? (
                    <div className="admin-widgets-empty">Мереж ще немає.</div>
                ) : (
                    <div className="admin-network-stats">
                        {networkRows.map((network) => (
                            <button
                                key={network.id}
                                type="button"
                                className={`admin-network-stats__row ${network.status ? "" : "is-inactive"}`}
                                onClick={() => onNavigate?.("networks")}
                            >
                                <div className="admin-network-stats__name">
                                    <strong>{network.network_name || `Мережа #${network.id}`}</strong>
                                    <span>
                                        {network.status ? "Активна" : "Вимкнена"}
                                        {network.deployment_mode === "self_hosted" ? " · self-hosted" : ""}
                                    </span>
                                </div>
                                <div className="admin-network-stats__metrics">
                                    <span>
                                        <b>{Number(network.clients_count) || 0}</b>
                                        клієнтів
                                    </span>
                                    <span>
                                        <b>{Number(network.operators_count) || 0}</b>
                                        операторів
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section className="admin-widgets-section">
                <div className="admin-widgets-section__head">
                    <h3>Останні новини</h3>
                    <button type="button" className="admin-widgets-link" onClick={() => onNavigate?.("news")}>
                        Усі новини
                    </button>
                </div>
                {recentNews.length === 0 ? (
                    <div className="admin-widgets-empty">Новин ще немає.</div>
                ) : (
                    <div className="admin-widgets-list">
                        {recentNews.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className="admin-widgets-list__item"
                                onClick={() => onNavigate?.("news")}
                            >
                                <strong>{item.title || "Без назви"}</strong>
                                <span>{previewText(item.content)}</span>
                                <em>
                                    {[item.network_name, formatDate(item.created_at || item.dtlm)].filter(Boolean).join(" · ")}
                                </em>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section className="admin-widgets-section">
                <div className="admin-widgets-section__head">
                    <h3>Останні опитування</h3>
                    <button type="button" className="admin-widgets-link" onClick={() => onNavigate?.("polls")}>
                        Усі опитування
                    </button>
                </div>
                {recentPolls.length === 0 ? (
                    <div className="admin-widgets-empty">Опитувань ще немає.</div>
                ) : (
                    <div className="admin-widgets-list">
                        {recentPolls.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className="admin-widgets-list__item"
                                onClick={() => onNavigate?.("polls")}
                            >
                                <strong>{previewText(item.question, 110)}</strong>
                                <span>
                                    Відповідей: {Number(item.responded_count) || 0}
                                    {item.total_clients_network != null
                                        ? ` / ${Number(item.total_clients_network) || 0} клієнтів`
                                        : ""}
                                </span>
                                <em>{formatDate(item.created_at || item.expires_at)}</em>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section className="admin-widgets-section">
                <div className="admin-widgets-section__head">
                    <h3>Останні відгуки</h3>
                    <button type="button" className="admin-widgets-link" onClick={() => onNavigate?.("feedbacks")}>
                        Усі відгуки
                    </button>
                </div>
                {recentFeedbacks.length === 0 ? (
                    <div className="admin-widgets-empty">Відгуків ще немає.</div>
                ) : (
                    <div className="admin-widgets-list">
                        {recentFeedbacks.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className="admin-widgets-list__item"
                                onClick={() => onNavigate?.("feedbacks")}
                            >
                                <strong>{String(item.status || "new").toUpperCase()}</strong>
                                <span>{previewText(item.message)}</span>
                                <em>{formatDate(item.created_at)}</em>
                            </button>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
